import { useState } from "react";
import { Button, Card, ErrorState, Field, ProblemError, Skeleton } from "@csa/web-foundation";
import { useT } from "../i18n/index";
import type { MessageKey } from "../i18n/dictionaries";
import { usePartyPrincipal } from "../auth/party-principal";
import { Icon, type IconName } from "../ui/icons";
import { useCreateQuoteRequest } from "./use-quote-request";
import type {
  CustomerQuoteRequestInput,
  DwellingStructure,
  QuoteLine,
  SgkStatus,
} from "../api/contracts";

// Teklif Al — the authenticated re-quote surface (System-1: "giriş yaptıktan sonra kendi
// sayfasından teklif isteyebilir"). Calm and reassuring: a returning, KNOWN customer, so
// identity is NEVER re-entered — a read-only "Kimin için" summary from the masked profile
// (GET /v1/customer/profile) shows WHO the request is for. The customer picks a line and
// fills only that line's fields; submit → POST /v1/customer/quote-requests with the matching
// block ONLY (line-binding, mirrors customerQuoteRequestSchema). Outcome is a calm
// confirmation — NEVER a price/offer (the moat). No identity fields ever leave the browser.

const PRODUCTS: readonly QuoteLine[] = ["trafik", "kasko", "dask", "seyahat", "saglik"];
const PRODUCT_ICON: Record<QuoteLine, IconName> = {
  trafik: "vehicle",
  kasko: "vehicle",
  dask: "household",
  seyahat: "calendar",
  saglik: "protection",
};
const STRUCTURES: readonly DwellingStructure[] = ["betonarme", "celik", "yigma", "diger"];
const SGK_OPTIONS: readonly SgkStatus[] = ["sgkli", "sgksiz", "bilinmiyor"];

const PLATE_RE = /^[0-9]{2}\s?[A-Z]{1,3}\s?[0-9]{2,4}$/;
const UAVT_RE = /^\d{10}$/;

const isVehicle = (p: QuoteLine): boolean => p === "trafik" || p === "kasko";

interface FormState {
  // vehicle (trafik/kasko)
  plate: string;
  vin: string;
  // dwelling (dask)
  address: string;
  uavtCode: string;
  grossM2: string;
  structure: string;
  buildYear: string;
  floor: string;
  // travel (seyahat)
  destination: string;
  startDate: string;
  endDate: string;
  travelers: string;
  // health (saglik)
  sgkStatus: string;
  hospitalPref: string;
  saglikAcikRiza: boolean;
}

const emptyForm = (): FormState => ({
  plate: "",
  vin: "",
  address: "",
  uavtCode: "",
  grossM2: "",
  structure: "",
  buildYear: "",
  floor: "",
  destination: "",
  startDate: "",
  endDate: "",
  travelers: "",
  sgkStatus: "",
  hospitalPref: "",
  saglikAcikRiza: false,
});

/** Line-binding: build a body carrying ONLY the picked product's block (never smuggle
 *  another line's attributes), mirroring customerQuoteRequestSchema. Empty fields are
 *  omitted so `.strict()` optional fields stay clean. */
function buildBody(product: QuoteLine, f: FormState): CustomerQuoteRequestInput {
  const body: CustomerQuoteRequestInput = { product };
  const s = (v: string) => v.trim();
  if (isVehicle(product)) {
    const vehicle: NonNullable<CustomerQuoteRequestInput["vehicle"]> = {};
    if (s(f.plate)) vehicle.plate = s(f.plate).toUpperCase();
    if (s(f.vin)) vehicle.vin = s(f.vin).toUpperCase();
    body.vehicle = vehicle;
  } else if (product === "dask") {
    const dwelling: NonNullable<CustomerQuoteRequestInput["dwelling"]> = {};
    if (s(f.address)) dwelling.address = s(f.address);
    if (s(f.uavtCode)) dwelling.uavtCode = s(f.uavtCode);
    if (s(f.grossM2)) dwelling.grossM2 = Number(f.grossM2);
    if (f.structure) dwelling.structure = f.structure as DwellingStructure;
    if (s(f.buildYear)) dwelling.buildYear = Number(f.buildYear);
    if (s(f.floor)) dwelling.floor = s(f.floor);
    body.dwelling = dwelling;
  } else if (product === "seyahat") {
    const travel: NonNullable<CustomerQuoteRequestInput["travel"]> = {};
    if (s(f.destination)) travel.destination = s(f.destination);
    if (s(f.startDate)) travel.startDate = s(f.startDate);
    if (s(f.endDate)) travel.endDate = s(f.endDate);
    if (s(f.travelers)) travel.travelers = Number(f.travelers);
    body.travel = travel;
  } else if (product === "saglik") {
    const health: NonNullable<CustomerQuoteRequestInput["health"]> = {
      // The health declaration itself stays offline (spec §5) — this ACKs that. The
      // special-category açık-rıza is the consent below (required for this line only).
      declarationOffline: true,
    };
    if (f.sgkStatus) health.sgkStatus = f.sgkStatus as SgkStatus;
    if (s(f.hospitalPref)) health.hospitalPref = s(f.hospitalPref);
    body.health = health;
    body.consents = { saglikAcikRiza: true };
  }
  return body;
}

/** Client-side required/format checks — a calm nudge before submit (the server is the real
 *  boundary). Returns the first blocking message key, or null when the line is ready. */
function validate(product: QuoteLine, f: FormState): MessageKey | null {
  if (isVehicle(product)) {
    const plate = f.plate.trim().toUpperCase();
    if (!plate && !f.vin.trim()) return "qr.err.vehicleRequired";
    if (plate && !PLATE_RE.test(plate)) return "qr.err.plateFormat";
  } else if (product === "dask") {
    if (!f.address.trim()) return "qr.err.addressRequired";
    if (f.uavtCode.trim() && !UAVT_RE.test(f.uavtCode.trim())) return "qr.err.uavtFormat";
  } else if (product === "seyahat") {
    if (!f.destination.trim()) return "qr.err.destinationRequired";
  } else if (product === "saglik") {
    if (!f.saglikAcikRiza) return "qr.err.acikRizaRequired";
  }
  return null;
}

export function QuoteRequest() {
  const { t } = useT();
  const tk = t as (k: MessageKey) => string;
  const { profile, isLoading, error } = usePartyPrincipal();
  const create = useCreateQuoteRequest();

  const [product, setProduct] = useState<QuoteLine | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [localError, setLocalError] = useState<MessageKey | null>(null);
  const [done, setDone] = useState(false);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const pick = (p: QuoteLine) => {
    setProduct(p);
    setForm(emptyForm());
    setLocalError(null);
    create.reset();
  };

  const startOver = () => {
    setProduct(null);
    setForm(emptyForm());
    setLocalError(null);
    setDone(false);
    create.reset();
  };

  const onSubmit = () => {
    if (!product) return;
    const problem = validate(product, form);
    setLocalError(problem);
    if (problem) return;
    create.mutate(buildBody(product, form), { onSuccess: () => setDone(true) });
  };

  // Server errors: 429 → the calm "try again shortly"; otherwise the real ProblemJson detail.
  const serverErr =
    create.error instanceof ProblemError
      ? create.error.status === 429
        ? tk("qr.err.rate")
        : create.error.problem.detail || tk("qr.err.generic")
      : create.error
        ? tk("qr.err.generic")
        : null;

  if (isLoading) {
    return (
      <Card data-testid="quote-loading">
        <Skeleton lines={5} />
      </Card>
    );
  }
  if (error || !profile) {
    return (
      <ErrorState>
        <p data-testid="quote-profile-error">{tk("qr.profileError")}</p>
      </ErrorState>
    );
  }

  // ── Success — a calm confirmation, NO price/offer (the moat) ──
  if (done) {
    return (
      <div className="stack" data-testid="quote-request">
        <div className="cx-pagehead">
          <div>
            <h1>{tk("qr.title")}</h1>
          </div>
        </div>
        <Card data-testid="quote-success">
          <div className="cx-card__head">
            <span className="cx-tile cx-tile--green" aria-hidden="true">
              <Icon name="protection" size={22} />
            </span>
            <div className="cx-card__headmain">
              <span className="cx-card__title">{tk("qr.successTitle")}</span>
              <span className="cx-card__sub">{tk("qr.successBody")}</span>
            </div>
          </div>
          <div className="cx-cardcta">
            <Button variant="secondary" data-testid="quote-new" onClick={startOver}>
              {tk("qr.newRequest")}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="stack" data-testid="quote-request">
      <div className="cx-pagehead">
        <div>
          <h1>{tk("qr.title")}</h1>
          <p className="cx-pagehead__sub">{tk("qr.subtitle")}</p>
        </div>
      </div>

      {/* ── Kimin için — read-only identity from the masked profile (never re-entered) ── */}
      <Card data-testid="quote-identity">
        <h2>{tk("qr.forWhom")}</h2>
        <div className="profile-field">
          <span className="muted small">{tk("qr.name")}</span>
          <strong data-testid="quote-identity-name">
            {profile.firstName} {profile.lastName}
          </strong>
        </div>
        {profile.phone ? (
          <div className="profile-field">
            <span className="muted small">{tk("qr.phone")}</span>
            <span data-testid="quote-identity-phone">{profile.phone}</span>
          </div>
        ) : null}
        {profile.email ? (
          <div className="profile-field">
            <span className="muted small">{tk("qr.email")}</span>
            <span data-testid="quote-identity-email">{profile.email}</span>
          </div>
        ) : null}
        <p className="muted small" data-testid="quote-identity-note">
          <span aria-hidden="true">ⓘ </span>
          {tk("qr.forWhomNote")}
        </p>
      </Card>

      {/* ── Line picker ── */}
      <Card data-testid="quote-picker">
        <h2>{tk("qr.pickProduct")}</h2>
        <div className="qr-picker" role="group" aria-label={tk("qr.pickProduct")}>
          {PRODUCTS.map((p) => (
            <button
              key={p}
              type="button"
              className={`qr-pick${product === p ? " is-selected" : ""}`}
              aria-pressed={product === p}
              data-testid={`pick-${p}`}
              onClick={() => pick(p)}
            >
              <span className="cx-tile cx-tile--blue" aria-hidden="true">
                <Icon name={PRODUCT_ICON[p]} size={20} />
              </span>
              <span>{tk(`qr.product.${p}` as MessageKey)}</span>
            </button>
          ))}
        </div>
      </Card>

      {/* ── Line-specific fields ── */}
      {product ? (
        <Card data-testid="quote-fields">
          <h2>{tk(`qr.product.${product}` as MessageKey)}</h2>
          <p className="muted small">{tk("qr.fieldsNote")}</p>

          <form
            className="profile-form"
            data-testid="quote-form"
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit();
            }}
          >
            {isVehicle(product) ? (
              <>
                <Field
                  label={tk("qr.f.plate")}
                  value={form.plate}
                  placeholder="34 ABC 123"
                  hint={tk("qr.f.plateHint")}
                  data-testid="input-plate"
                  onChange={(e) => set("plate", e.target.value)}
                />
                <Field
                  label={tk("qr.f.vin")}
                  value={form.vin}
                  hint={tk("qr.f.vinHint")}
                  data-testid="input-vin"
                  onChange={(e) => set("vin", e.target.value)}
                />
              </>
            ) : null}

            {product === "dask" ? (
              <>
                <Field
                  label={tk("qr.f.address")}
                  value={form.address}
                  data-testid="input-address"
                  onChange={(e) => set("address", e.target.value)}
                />
                <Field
                  label={tk("qr.f.uavt")}
                  value={form.uavtCode}
                  inputMode="numeric"
                  hint={tk("qr.f.uavtHint")}
                  data-testid="input-uavt"
                  onChange={(e) => set("uavtCode", e.target.value)}
                />
                <Field
                  label={tk("qr.f.grossM2")}
                  type="number"
                  value={form.grossM2}
                  data-testid="input-grossM2"
                  onChange={(e) => set("grossM2", e.target.value)}
                />
                <SelectField
                  label={tk("qr.f.structure")}
                  value={form.structure}
                  placeholder={tk("qr.f.choose")}
                  options={STRUCTURES.map((v) => ({
                    value: v,
                    label: tk(`qr.structure.${v}` as MessageKey),
                  }))}
                  testId="input-structure"
                  onChange={(v) => set("structure", v)}
                />
                <Field
                  label={tk("qr.f.buildYear")}
                  type="number"
                  value={form.buildYear}
                  data-testid="input-buildYear"
                  onChange={(e) => set("buildYear", e.target.value)}
                />
                <Field
                  label={tk("qr.f.floor")}
                  value={form.floor}
                  data-testid="input-floor"
                  onChange={(e) => set("floor", e.target.value)}
                />
              </>
            ) : null}

            {product === "seyahat" ? (
              <>
                <Field
                  label={tk("qr.f.destination")}
                  value={form.destination}
                  data-testid="input-destination"
                  onChange={(e) => set("destination", e.target.value)}
                />
                <Field
                  label={tk("qr.f.startDate")}
                  type="date"
                  value={form.startDate}
                  data-testid="input-startDate"
                  onChange={(e) => set("startDate", e.target.value)}
                />
                <Field
                  label={tk("qr.f.endDate")}
                  type="date"
                  value={form.endDate}
                  data-testid="input-endDate"
                  onChange={(e) => set("endDate", e.target.value)}
                />
                <Field
                  label={tk("qr.f.travelers")}
                  type="number"
                  value={form.travelers}
                  data-testid="input-travelers"
                  onChange={(e) => set("travelers", e.target.value)}
                />
              </>
            ) : null}

            {product === "saglik" ? (
              <>
                <SelectField
                  label={tk("qr.f.sgk")}
                  value={form.sgkStatus}
                  placeholder={tk("qr.f.choose")}
                  options={SGK_OPTIONS.map((v) => ({
                    value: v,
                    label: tk(`qr.sgk.${v}` as MessageKey),
                  }))}
                  testId="input-sgk"
                  onChange={(v) => set("sgkStatus", v)}
                />
                <Field
                  label={tk("qr.f.hospital")}
                  value={form.hospitalPref}
                  data-testid="input-hospital"
                  onChange={(e) => set("hospitalPref", e.target.value)}
                />
                <p className="muted small" data-testid="declaration-note">
                  {tk("qr.f.declarationNote")}
                </p>
                <label className="qr-consent" data-testid="acikriza-row">
                  <input
                    type="checkbox"
                    checked={form.saglikAcikRiza}
                    data-testid="input-acikriza"
                    onChange={(e) => set("saglikAcikRiza", e.target.checked)}
                  />
                  <span>{tk("qr.f.acikRiza")}</span>
                </label>
              </>
            ) : null}

            {localError ? (
              <p className="field-error" role="alert" data-testid="quote-validation-error">
                {tk(localError)}
              </p>
            ) : null}
            {serverErr ? (
              <p className="field-error" role="alert" data-testid="quote-server-error">
                {serverErr}
              </p>
            ) : null}

            <div className="cx-cardcta">
              <Button
                type="submit"
                variant="primary"
                data-testid="quote-submit"
                disabled={create.isPending}
              >
                {create.isPending ? tk("qr.submitting") : tk("qr.submit")}
              </Button>
            </div>
          </form>
        </Card>
      ) : null}
    </div>
  );
}

// A calm labeled <select> on the same ui-field/ui-input primitives the Field uses (the
// foundation kit ships no Select; this keeps the DS consistent without inventing one).
function SelectField({
  label,
  value,
  placeholder,
  options,
  testId,
  onChange,
}: {
  label: string;
  value: string;
  placeholder: string;
  options: { value: string; label: string }[];
  testId: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="ui-field">
      <label>
        {label}
        <select
          className="ui-input"
          value={value}
          data-testid={testId}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">{placeholder}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
